import { inject, injectable } from 'inversify';

import { Enums, UIDtoFactory } from '@project44-models/iam-gateway';

import { AllTenantAgnosticUnifiedRolesQuery } from '../../../Tenant/application/useCase';
import { TenantUnifiedRolesQuery } from '../../../Tenant/application/useCase/TenantSpecificUnifiedRolesQuery.useCase';
import { UnifiedRolesFactory } from '../../../UnifiedRoles/infrastructure/factories/UnifiedRoles.factory';
import {
  UserTenantAgnosticUnifiedRolesQuery,
  UserTenantAgnosticUnifiedRolesUpdateCommand,
  UserTenantSpecificTenantAdminPolicyUpdateCommand,
  UserTenantSpecificUnifiedRolesQuery,
  UserTenantSpecificUnifiedRolesUpdateCommand,
} from '../../../User/application/useCase';
import { UserTenantAgnosticRoles } from '../../domain/aggregates/UserTenantAgnosticRoles.aggregate';
import { UserTenantSpecificRoles } from '../../domain/aggregates/UserTenantSpecificRoles.aggregate';
import {
  IUserFactory,
  IUserTenantAgnosticRolesConstructor,
  IUserTenantSpecificRolesConstructor,
} from '../../domain/interfaces';

@injectable()
export class UserFactory implements IUserFactory {
  constructor(
    @inject(UnifiedRolesFactory)
    private readonly unifiedRolesFactory: UnifiedRolesFactory,

    @inject(AllTenantAgnosticUnifiedRolesQuery)
    private readonly allTenantAgnosticUnifiedRolesQuery: AllTenantAgnosticUnifiedRolesQuery,

    @inject(UserTenantAgnosticUnifiedRolesQuery)
    private readonly userTenantAgnosticUnifiedRolesQuery: UserTenantAgnosticUnifiedRolesQuery,

    @inject(UserTenantAgnosticUnifiedRolesUpdateCommand)
    private readonly userTenantAgnosticUnifiedRolesUpdateCommand: UserTenantAgnosticUnifiedRolesUpdateCommand,

    @inject(TenantUnifiedRolesQuery)
    private readonly tenantUnifiedRolesQueryUserCase: TenantUnifiedRolesQuery,

    @inject(UserTenantSpecificUnifiedRolesQuery)
    private readonly userTenantSpecificUnifiedRolesQuery: UserTenantSpecificUnifiedRolesQuery,

    @inject(UserTenantSpecificUnifiedRolesUpdateCommand)
    private readonly userTenantSpecificUnifiedRolesUpdateCommand: UserTenantSpecificUnifiedRolesUpdateCommand,

    @inject(UserTenantSpecificTenantAdminPolicyUpdateCommand)
    private readonly userTenantSpecificTenantAdminPolicyUpdateCommand: UserTenantSpecificTenantAdminPolicyUpdateCommand,
  ) {}

  public readonly createUserTenantAgnosticRoles: IUserFactory['createUserTenantAgnosticRoles'] =
    ({ user, userPrincipal, tenant }) => {
      const aggregatePayload: IUserTenantAgnosticRolesConstructor = {
        userPrincipal,
        user,
        tenant,
        allTenantAgnosticUnifiedRolesQuery:
          this.allTenantAgnosticUnifiedRolesQuery,
        userTenantAgnosticUnifiedRolesQuery:
          this.userTenantAgnosticUnifiedRolesQuery,
        userTenantAgnosticUnifiedRolesUpdateCommand:
          this.userTenantAgnosticUnifiedRolesUpdateCommand,
        unifiedRolesFactory: this.unifiedRolesFactory,
      };

      return UserTenantAgnosticRoles.create(aggregatePayload);
    };

  public readonly createUserTenantSpecificRoles: IUserFactory['createUserTenantSpecificRoles'] =
    ({ user, userPrincipal, tenant }) => {
      const aggregatePayload: IUserTenantSpecificRolesConstructor = {
        userPrincipal,
        user,
        tenant,
        tenantUnifiedRolesQuery: this.tenantUnifiedRolesQueryUserCase,
        changeUserTenantSpecificAdminRolesType:
          this.userTenantSpecificTenantAdminPolicyUpdateCommand,
        userTenantSpecificUnifiedRolesQuery:
          this.userTenantSpecificUnifiedRolesQuery,
        userTenantSpecificUnifiedRolesUpdateCommand:
          this.userTenantSpecificUnifiedRolesUpdateCommand,
        createUnifiedRolesEditorPolicy:
          this.unifiedRolesFactory.createUnifiedRolesEditorPolicy,
      };

      return UserTenantSpecificRoles.create(aggregatePayload);
    };

  public readonly createUserDto: IUserFactory['createUserDto'] = (
    maybeUserPayload,
  ) => {
    return UIDtoFactory.create(
      Enums.UiDtoFactoryEnum.USER,
      maybeUserPayload || {},
    );
  };
}
